/* global debounce */

if (!customElements.get('predictive-search')) {
  // If configured (Content > Metaobjects has nothing to do with this -
  // it's the theme setting group "Meilisearch filtering"), the instant
  // dropdown queries Meilisearch directly instead of Shopify's native
  // predictive search, for real typo tolerance (e.g. "Abnea" -> "Abena")
  // that native search doesn't have. Products only, same accepted
  // trade-off as the full /search results page - collections/pages/
  // articles no longer appear in the dropdown specifically. Falls back
  // to the original native behavior untouched if unconfigured.
  const meilisearchConfigEl = document.getElementById('llm-predictive-search-config');
  const meilisearchConfig = meilisearchConfigEl ? JSON.parse(meilisearchConfigEl.textContent) : null;

  // See searchSuggestions() for why this exists and what belongs in it.
  const SUGGESTION_STOPWORDS = new Set(
    ('the and for with of in a an to by size count pack box case ct pk each disposable adult ' +
      'unisex new premium essential classic select deluxe original style pre cut fit trim').split(' ')
  );

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) {
        this.showPopular();
        return;
      }

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Shows the curated "Popular searches + Popular products" empty state
     * (server-rendered into .js-predictive-empty by predictive-search.liquid)
     * when the field is focused/cleared while empty. Only the header search
     * opts in (show_popular), so on any other search instance the element is
     * absent and this is a no-op.
     */
    showPopular() {
      const empty = this.querySelector('.js-predictive-empty');
      if (!empty || !empty.innerHTML.trim()) return false;
      this.results.innerHTML = empty.innerHTML;
      /* "Pick up where you left off" + "Recent searches" go in ABOVE the curated sections. They
         are per-visitor, so they cannot come from Liquid — llm-search-recent.js reads them from
         localStorage and injects them here. Absent (or empty history) and this does nothing, which
         is the first-time-visitor case. */
      if (window.llmSearchRecent) window.llmSearchRecent.prepend(this.results);
      this.removeAttribute('results');
      this.open();
      return true;
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If the field is empty after a key press, fall back to the Popular
      // empty-state (header search) or just close (everywhere else).
      if (this.getQuery().length) return;
      if (!this.showPopular()) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();

      if (meilisearchConfig) {
        await this.getResultsFromMeilisearch(searchTerm);
        return;
      }

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        this.close();
        throw error;
      }
    }

    /**
     * Meilisearch-backed results path (products only) - see
     * meilisearchConfig above. Builds markup matching the native
     * predictive-search-tab-panel.liquid structure closely enough that
     * keyboard nav/ARIA (which only cares about .predictive-search__item
     * and aria-selected) keeps working unchanged.
     * @param {string} searchTerm - Search query.
     */
    async getResultsFromMeilisearch(searchTerm) {
      const queryKey = `meili:${searchTerm.toLowerCase()}`;
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        // Products from Meilisearch + matching collections from Shopify's native suggest
        // API (the Meili index is products-only), fetched in parallel.
        const [data, collections] = await Promise.all([
          this.fetchMeilisearchProducts(searchTerm),
          this.fetchCollectionMatches(searchTerm),
        ]);

        const resultsMarkup = this.buildMeilisearchMarkup(data.hits || [], collections, searchTerm);
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        this.close();
        throw error;
      }
    }

    /**
     * Products from Meilisearch, asking for PRECISION first and widening only if that
     * finds nothing.
     *
     *   'all'        every query word must match. What a shopper expects.
     *   'frequency'  drops words until something matches. Needed because the default
     *                ("last") never drops the FIRST word, so one unrecognised leading
     *                word ("nonrinse shampoo and body wash") returned zero results.
     *
     * Running 'frequency' unconditionally made specific searches noisy: "eye patch"
     * returned 21 products (measured 2026-09-17) because it dropped "eye" and searched
     * "patch" — pain-relief patches trailing the 6 real eye patches. 'all' returns
     * exactly those 6. Trying 'all' first keeps that precision without losing the
     * unknown-word rescue, and only costs a second request when the first finds nothing.
     *
     * Vocabulary itself is handled by Meilisearch's own `synonyms` index setting
     * (SEARCH_SYNONYMS in the sync worker), not client-side.
     * @param {string} searchTerm
     * @returns {Promise<object>} the Meilisearch response body
     */
    async fetchMeilisearchProducts(searchTerm) {
      let data = { hits: [] };
      /* ⛔ NEVER WIDEN A PART NUMBER — same rule and same reasoning as the one in
         llm-meilisearch-search.js, which this dropdown must stay in step with. Owner, 2026-10-20:
         "M59988-10" returned 1,155 results on the results page because 'frequency' dropped tokens
         until it matched "10". A code either matches exactly or there is nothing useful to widen
         to; the rescue is for PROSE with one unknown word.
         ⚠️ Both surfaces run this rescue, so fixing only the results page would leave the dropdown
         suggesting diapers for a part number while the page it leads to says no results. */
      const q = String(searchTerm || '').trim();
      const looksLikeCode = q !== '' && !/\s/.test(q) && /\d/.test(q);
      for (const matchingStrategy of (looksLikeCode ? ['all'] : ['all', 'frequency'])) {
        const res = await fetch(`${meilisearchConfig.host}/indexes/${meilisearchConfig.index}/search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${meilisearchConfig.searchKey}`,
          },
          body: JSON.stringify({
            q: searchTerm,
            /* Ask for more than we show. The visible product list is still
               meilisearchConfig.limit (sliced by the caller), but the SUGGESTED SEARCHES
               above it are derived from matched product titles, and six hits is too thin a
               sample — a query like "nort" would offer two phrases instead of five. Costs
               nothing extra: same request, same response. */
            limit: Math.max(meilisearchConfig.limit || 6, 24),
            matchingStrategy,
          }),
        });
        if (!res.ok) throw new Error(res.status);
        data = await res.json();
        if ((data.hits || []).length > 0) break;
      }
      return data;
    }

    /**
     * Matching COLLECTIONS from Shopify's native predictive-search API (shown at the top
     * of the results as a small image + name). The Meilisearch index is products-only, so
     * this is a separate lookup. Fails soft (returns []) so products still render on error.
     * @param {string} searchTerm
     * @returns {Promise<Array>}
     */
    async fetchCollectionMatches(searchTerm) {
      try {
        // PAGES are requested alongside collections (owner 2026-08-18): searching
        // "samples" used to return nothing at all, yet /pages/samples is a real,
        // curated page listing every product with a free sample. The Meilisearch
        // index is products-only, so pages can only come from this native endpoint.
        const url =
          `/search/suggest.json?q=${encodeURIComponent(searchTerm)}` +
          '&resources[type]=collection,page&resources[limit]=3';
        const res = await fetch(url);
        if (!res.ok) return [];
        const json = await res.json();
        const results = json?.resources?.results || {};
        // Pages are returned in the same shape (title + url) and rendered by the same
        // markup builder, so they're merged into one "shortcuts" list. Pages first:
        // when someone searches "samples" the page IS the answer.
        return [
          ...(results.pages || []).map((p) => ({ ...p, llmIsPage: true })),
          ...(results.collections || []),
        ];
      } catch (e) {
        return [];
      }
    }

    /**
     * Star-rating badge for a product result, mimicking the product-card badge
     * (snippets/llm-rating-badge.liquid) — "4.8 ★★★★★ (1275)", filled via the shared
     * .rating__stars --rating CSS (loaded globally). A <span> (not the card's <a>) since
     * the whole result is already a link. Uses the hit's rating / rating_rounded / rating_count.
     * @param {Object} hit
     * @returns {string}
     */
    ratingBadgeHtml(hit) {
      const count = hit.rating_count || 0;
      if (count > 0 && typeof hit.rating === 'number') {
        // Round to the nearest half-star for the visual fill (matches the Liquid badge).
        const rv = hit.rating;
        const dec = rv % 1;
        let stars = Math.floor(rv);
        if (dec >= 0.3 && dec <= 0.7) stars += 0.5;
        else if (dec > 0.7) stars = Math.ceil(rv);
        const shown = (hit.rating_rounded != null ? hit.rating_rounded : rv).toFixed(1);
        return `<span class="llm-rating-badge predictive-result__rating inline-flex items-center">
          <span class="llm-rating-badge__value" aria-hidden="true">${shown}</span>
          <span class="rating__stars llm-rating-badge__stars" aria-hidden="true" style="--rating: ${stars}; --rating-max: 5;">★★★★★</span>
          <span class="llm-rating-badge__count" aria-hidden="true">(<span class="llm-rating-badge__count-num">${count}</span>)</span>
        </span>`;
      }
      return `<span class="llm-rating-badge llm-rating-badge--empty predictive-result__rating inline-flex items-center">
        <span class="rating__stars llm-rating-badge__stars" aria-hidden="true" style="--rating: 0; --rating-max: 5;">★★★★★</span>
        <span class="llm-rating-badge__count" aria-hidden="true">(<span class="llm-rating-badge__count-num">0</span>)</span>
      </span>`;
    }

    /**
     * @param {Array} hits - Meilisearch product hits.
     * @param {string} searchTerm - Search query (for the no-results message).
     * @returns {string} HTML matching the native predictive-search markup structure.
     */
    /* Words that are in the titles but that nobody puts in a search box: tier adjectives
       ("premium", "essential"), packaging nouns ("pack", "count", "case") and filler. Left in, they
       produced "tranquility premium" — the owner's example of a suggestion no real customer would
       type. Keep this list SHORT and only for words that carry no search intent; anything that
       identifies a product line ("air-plus", "megamax", "overnight") must stay. */
    /* SUGGESTED SEARCHES — query completions, the way Amazon and Chewy offer them (owner
       2026-09-05). Typing "nort" offers "northshore megamax", "northshore gosupreme", … as
       clickable searches, above the product results.

       Derived from the titles of the products Meilisearch just matched, NOT from a curated list and
       NOT from search logs. That means they are always real, always in stock-ish, and need no
       maintenance — a suggestion can never lead to zero results, because it came from a product
       that matched. The trade-off is that they describe the catalogue rather than what people
       actually type; if real query logs become available, that is the better source.

       Built in two stages, because doing it in one produced exactly the junk the owner called out
       ("nobody searches 'Tranquility Premium'" — that came from blindly taking the next word in the
       title, which is usually a marketing tier adjective):

         1. COMPLETE the typed word from the matched titles: "nort" -> "northshore". Without this,
            appending anything to the raw query gives "nort adult diapers".
         2. APPEND a term people actually search, ranked by popularity. Two sources, interleaved:
            the products' `product_type` ("adult diapers", "incontinence pads") — which is almost
            always a real search phrase — and distinctive words from the titles ("megamax",
            "overnight"), with tier adjectives and packaging nouns filtered out.

       Popularity is `rating_count`, log-damped. The index carries no purchase data, and review
       count is the closest proxy it has for what sells; log() stops one 972-review product from
       drowning out everything else. Verified against real queries before shipping:
         tranquility -> adult diapers / briefs / incontinence pads / air-plus
         nort        -> northshore adult diapers / megamax / supreme / gosupreme
         betterdry   -> adult diapers / overnight
       Once real search-query logging exists, THAT should rank these and this becomes the fallback
       for queries with no history. */
    searchSuggestions(hits, searchTerm) {
      const q = String(searchTerm || '').toLowerCase().replace(/\s+/g, ' ').trim();
      if (q.length < 2) return [];
      /* Only offer completions while the query is still SHORT. These work by appending a term to
         what has been typed, which is helpful for "nort" or "tranquility" and absurd once the
         customer has written a real sentence — "overnight adult diapers extra large" came back as
         "overnight adult diapers extra large incontinence pads" (caught in testing 2026-09-05).
         Past two words the customer has already said what they want; the product results below are
         the useful thing, not more words. */
      if (q.split(' ').length > 2) return [];

      const norm = (s) =>
        String(s || '').toLowerCase().replace(/[^a-z0-9\s-]/g, ' ').replace(/\s+/g, ' ').trim();
      // log-damped so review-count differences rank without one product dominating.
      const pop = (h) => Math.log(1 + (h.rating_count || 0));

      // --- 1. complete the typed word ---
      const qWords = q.split(' ').length;
      const baseScores = new Map();
      hits.forEach((h) => {
        const w = norm(h.title).split(' ');
        for (let i = 0; i < w.length; i += 1) {
          // Must start a WORD, so "ostomy" does not match inside "colostomy".
          if (w.slice(i).join(' ').indexOf(q) !== 0) continue;
          const b = w.slice(i, i + qWords).join(' ');
          if (b) baseScores.set(b, (baseScores.get(b) || 0) + pop(h));
          break;
        }
      });
      let base = q;
      let best = -1;
      baseScores.forEach((score, phrase) => {
        if (score > best) { best = score; base = phrase; }
      });
      const baseWords = new Set(base.split(' '));

      // --- 2a. product types ---
      const typeScore = new Map();
      hits.forEach((h) => {
        // "Bed Pads & Underpads" / "Bed Pads/Underpads" -> "bed pads". Nobody searches the pairing.
        const t = norm(String(h.product_type || '').split(/[&/]/)[0]);
        if (!t || t.indexOf(base) !== -1 || base.indexOf(t) !== -1) return;
        typeScore.set(t, (typeScore.get(t) || 0) + pop(h));
      });

      // --- 2b. distinctive title words ---
      const wordScore = new Map();
      hits.forEach((h) => {
        const seenHere = new Set();
        norm(h.title).split(' ').forEach((w) => {
          if (w.length < 3 || baseWords.has(w) || SUGGESTION_STOPWORDS.has(w) || seenHere.has(w)) return;
          seenHere.add(w);
          wordScore.set(w, (wordScore.get(w) || 0) + pop(h));
        });
      });

      const byScore = (m) => [...m.entries()].sort((a, b) => b[1] - a[1]).map((e) => e[0]);
      const types = byScore(typeScore);
      // Drop words already carried by a product-type phrase, so "pads" doesn't shadow "incontinence pads".
      const words = byScore(wordScore).filter((w) => !types.some((t) => t.split(' ').indexOf(w) !== -1));

      // Interleave so the list is neither all categories nor all brand lines.
      const picks = [];
      for (let i = 0; i < 4; i += 1) {
        if (types[i]) picks.push(types[i]);
        if (words[i]) picks.push(words[i]);
      }

      const seen = new Set();
      const out = [];
      picks.forEach((p) => {
        const phrase = `${base} ${p}`.trim();
        if (phrase === q || seen.has(phrase)) return;
        seen.add(phrase);
        out.push(phrase);
      });
      return out.slice(0, 5);
    }

    buildMeilisearchMarkup(hits, collections, searchTerm) {
      collections = collections || [];
      if (hits.length === 0 && collections.length === 0) {
        const message = (meilisearchConfig.noResultsText || 'No results found for "__TERMS__"').replace(
          '__TERMS__',
          searchTerm
        );
        return `<div class="predictive-search absolute bg-theme-bg text-theme-text text-start"><div class="predictive-search__no-results">${this.escapeHtml(message)}</div></div>`;
      }

      // Collection matches at the TOP (small image + name), from Shopify's suggest API.
      const collectionItems = collections
        .map((c) => {
          const img =
            c.featured_image && c.featured_image.url
              ? `<img src="${this.escapeAttr(c.featured_image.url)}" width="48" height="48" loading="eager" alt="${this.escapeAttr(c.title)}">`
              : '';
          // Pages come through this same list (see fetchCollectionMatches) and have no
          // featured_image, so the media box is simply empty for them - but the badge
          // must not lie about what the shopper is about to open.
          const badge = c.llmIsPage ? 'Page' : 'Collection';
          return `
            <li class="predictive-search__item predictive-search__item--collection" role="option">
              <a class="predictive-result predictive-result--collection flex items-center focus-inset js-search-link" href="${this.escapeAttr(c.url)}">
                <div class="predictive-result__media predictive-result__media--collection media relative">${img}</div>
                <div class="predictive-result__info flex-auto">
                  <h3 class="predictive-result__title h6">${this.escapeHtml(c.title)}</h3>
                  <div class="predictive-result__badge">${badge}</div>
                </div>
              </a>
            </li>`;
        })
        .join('');

      /* Suggested searches, above everything. The typed part stays regular and the completion is
         bold, so the eye lands on what each row ADDS to what is already typed — the convention both
         Amazon and Chewy use. Lowercase on purpose: these are searches, not product names. */
      const suggestions = this.searchSuggestions(hits, searchTerm);
      const typedQ = String(searchTerm || '').toLowerCase().replace(/\s+/g, ' ').trim();
      const suggestionItems = suggestions
        .map((phrase) => {
          /* Only split when the suggestion really does begin with what was typed. It usually does
             — but the typed word gets COMPLETED from the catalogue ("nort" -> "northshore"), and a
             fuzzy/typo match ("abnea" -> "abena") produces a phrase that does not start with the
             query at all. Slicing blindly there would bold the wrong characters. */
          const startsWithTyped = typedQ && phrase.indexOf(typedQ) === 0;
          const typed = startsWithTyped ? phrase.slice(0, typedQ.length) : '';
          const rest = startsWithTyped ? phrase.slice(typedQ.length) : phrase;
          return `
            <li class="predictive-search__item predictive-search__item--suggestion" role="option">
              <a class="predictive-result predictive-result--suggestion flex items-center focus-inset js-search-link" href="/search?q=${encodeURIComponent(phrase)}">
                <svg class="predictive-result__mag" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <circle cx="7" cy="7" r="4.5" stroke="currentColor" stroke-width="1.5"/>
                  <path d="M10.5 10.5L14 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
                <span class="predictive-result__suggestion">${this.escapeHtml(typed)}<b>${this.escapeHtml(rest)}</b></span>
              </a>
            </li>`;
        })
        .join('');

      /* Products are sliced back to the configured limit — the Meilisearch request above
         deliberately over-fetches to give the suggestions a decent sample. */
      const productItems = hits
        .slice(0, meilisearchConfig.limit || 6)
        .map((hit) => {
          const image = hit.image
            ? `<div class="predictive-result__media media relative"><img src="${this.escapeAttr(hit.image)}" width="112" height="112" loading="eager" class="predictive-result__media" alt="${this.escapeAttr(hit.title)}"></div>`
            : '<div class="predictive-result__media media relative"></div>';
          // Single lowest price (no range). hit.price_min already excludes sample/trial
          // variants (computed in the Meilisearch sync Worker's transform.js).
          const price = `$${hit.price_min.toFixed(2)}`;
          const stars = this.ratingBadgeHtml(hit);

          return `
            <li class="predictive-search__item" role="option">
              <a class="predictive-result flex items-start focus-inset js-search-link" href="/products/${this.escapeAttr(hit.handle)}">
                ${image}
                <div class="predictive-result__info flex-auto">
                  <h3 class="predictive-result__title h6">${this.escapeHtml(hit.title)}</h3>
                  ${stars}
                  <div class="predictive-result__sub-title text-sm">${this.escapeHtml(price)}</div>
                </div>
              </a>
            </li>`;
        })
        .join('');

      // Group headings only when both groups are present, so a single group reads cleanly.
      const collectionSection = collectionItems
        ? `${hits.length ? '<li class="predictive-search__group-heading" aria-hidden="true">Collections</li>' : ''}${collectionItems}`
        : '';
      const productSection = productItems
        ? `${collectionItems ? '<li class="predictive-search__group-heading" aria-hidden="true">Products</li>' : ''}${productItems}`
        : '';

      const viewAllText = meilisearchConfig.viewAllText || 'View all';

      return `
        <div class="predictive-search absolute bg-theme-bg text-theme-text text-start">
          <div class="predictive-search__scroller overflow-auto">
            <div class="predictive-search__results flex flex-col">
              <div role="tabpanel" tabindex="0">
                <ul class="predictive-search__list" role="list">
                  ${suggestionItems}
                  ${collectionSection}
                  ${productSection}
                  <li class="predictive-search__item js-submit" role="option">
                    <button type="submit" class="predictive-search__view-all link w-full text-start focus-inset has-ltr-icon js-search-link">
                      ${this.escapeHtml(viewAllText)}
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>`;
    }

    /**
     * For interpolating into HTML TEXT content (element bodies, not
     * attributes). Confirmed bug: escaping quotes here (or round-
     * tripping through textContent -> innerHTML, which does the same
     * thing) produced a literally visible "&quot;" in the no-results
     * message - quotes aren't special in text-node position, so leave
     * them as real characters and only escape what actually needs it.
     * @param {string} str - Raw string.
     * @returns {string} HTML-escaped string, safe for element text content.
     */
    escapeHtml(str) {
      return String(str ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    /**
     * For interpolating into a double-quoted HTML attribute value
     * (href/src/alt) - here the quote DOES need escaping, to avoid
     * breaking out of the attribute's own quoting.
     * @param {string} str - Raw string.
     * @returns {string} HTML-escaped string, safe for a double-quoted attribute value.
     */
    escapeAttr(str) {
      return this.escapeHtml(str).replace(/"/g, '&quot;');
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      /* ⛔ THE PROMPT GOES INSIDE THE PANEL, NOT BEFORE IT.

         This used to be `questionPrompt() + resultsMarkup`, which made the row a SIBLING of
         `.predictive-search` — and that panel is `position: absolute` on desktop. Measured
         2026-09-13: the row rendered at 47px tall in normal flow, sat completely UNDERNEATH the
         dropdown (elementFromPoint returned a product result, not the row), and so was invisible
         and unclickable. On desktop this feature had never once been usable.

         It was not harmless while invisible. `.js-search-results` is in the flow of `form.search`,
         so those 47px made the form 92px instead of 45px — and the Cancel button is positioned
         `top: 50%` against that form, which is why Cancel appeared shoved below the search field
         (owner, searching "McKesson Tubular Elastic Retainer Net Dressing").

         Mobile hid the bug: there the panel is `position: fixed; top: 60px`, so the in-flow row
         showed above it and looked right, and Cancel is a flex item rather than absolutely
         positioned — so neither symptom appeared on a phone.

         Into the SCROLLER so it scrolls with the results, matching "above the product suggestions".
         Falls back outward if the panel markup ever changes shape, because a missing prompt is a
         far smaller problem than a thrown exception in the search dropdown. */
      this.results.innerHTML = resultsMarkup;
      const prompt = this.questionPrompt();
      if (prompt) {
        const host = this.results.querySelector('.predictive-search__scroller')
          || this.results.querySelector('.predictive-search')
          || this.results;
        host.insertAdjacentHTML('afterbegin', prompt);
      }
      this.setAttribute('results', true);
      this.open();
    }

    /* A typed QUESTION gets an "answer this" row above the product suggestions.
       "will my insurance take care of the cost?" suggested products in the dropdown, because
       a keyword search on a question matches stray words like "cost" and "care" — the same
       noise the results page produced (owner 2026-08-20).
       The products stay: they are occasionally useful and removing them would be a bigger
       change than the problem warrants. This just offers the thing the customer actually
       asked for, first. Pressing it lands on the search page, where the assistant answers. */
    questionPrompt() {
      const q = (this.input && this.input.value || '').trim();

      /* ⚠️ "SIX OR MORE WORDS" IS NOT A QUESTION IN THIS CATALOGUE.

         That was the old fallback, and medical product names are long: "McKesson Tubular Elastic
         Retainer Net Dressing" is exactly six words, so a customer typing a PRODUCT NAME was
         offered `Get an answer: "McKesson Tubular Elastic Retainer Net Dressing"`. Nonsense, and
         common — long names are the norm here, not the exception.

         The fallback still earns its place: the case it was built for ("will my insurance take care
         of the cost?") is a real one, and plenty of genuine questions arrive without a question
         mark. So keep length as a signal but require it to sound like someone TALKING about
         themselves — a first/second-person pronoun. Product names never contain one; questions from
         customers almost always do ("what size for my father", "does this work with my pump"). */
      const words = q.split(/\s+/).length;
      const personal = /\b(i|me|my|mine|we|we're|our|us|you|your|he|she|him|her|his|their)\b/i.test(q);
      const isQuestion =
        /\?/.test(q)
        || /^\s*(will|can|could|would|do|does|did|is|are|am|should|how|what|why|when|where|who|which)\b/i.test(q)
        || (words >= 6 && personal);
      if (!q || !isQuestion) return '';
      const safe = q.replace(/[<>&"]/g, '');
      return '<a class="predictive-search__item predictive-search__item--question" ' +
        'href="/search?q=' + encodeURIComponent(q) + '">' +
        '<span aria-hidden="true">&#10022;</span> Get an answer: &ldquo;' + safe + '&rdquo;</a>';
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus({ preventScroll: true });
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
